import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import ParrotMark from '../components/common/ParrotMark'
import StatusBanner from '../components/common/StatusBanner'
import BazaarButton from '../components/site/BazaarButton'
import SiteShell from '../components/site/SiteShell'
import { MANIFEST_URL, UPDATES_URL, androidChips, parseManifest, webInstallState } from '../lib/download'
import { APP_VERSION, faVersion } from '../lib/version'
import { isIos, isStandalone, useInstallStore } from '../store/installStore'

/**
 * The download page (`/download`), reached from the landing's «دریافت اپ»:
 * the Android build on one card, the web app on the other, and enough to
 * choose between them. Copy in the `download` namespace — the site is
 * fully Persian or fully English.
 *
 * The Android card starts as a link to the install page, which always
 * describes the current build, and upgrades itself to a direct download once
 * `latest.json` has answered — so there is no moment where the button is
 * dead, and nothing about the build is typed into this file. The size comes
 * from a HEAD of the APK itself: the manifest does not carry it, and the
 * phone that reads that manifest must not have to learn a new key.
 */

// The landing's two buttons, so the pages read as one: Button.jsx's primary
// and secondary variants, on links rather than <button>s.
const PRIMARY =
  'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-3 font-medium text-white transition-colors hover:bg-brand-700 dark:text-slate-900'
const SECONDARY =
  'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-medium text-brand-700 ring-1 ring-brand-200 transition-colors hover:bg-brand-50 dark:bg-slate-800'
const CARD = 'flex flex-col rounded-2xl border border-line bg-surface p-5'
const CHIP = 'rounded-full bg-surface-2 px-3 py-1 text-xs text-ink-2'

/** The build the update channel announces right now: the manifest, then the APK's own size from a HEAD of the file. */
function useLatestBuild() {
  const [manifest, setManifest] = useState(null)
  const [sizeBytes, setSizeBytes] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    async function load() {
      try {
        const response = await fetch(MANIFEST_URL, {
          signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
        const parsed = response.ok ? parseManifest(await response.json()) : null
        if (!parsed || signal.aborted) return
        setManifest(parsed)
        // The same-origin path rather than the manifest's absolute URL: in
        // production they are one file, and in development the path goes
        // through the dev server's /updates proxy (vite.config.js), where a
        // cross-origin HEAD would come back with no readable headers.
        const head = await fetch(new URL(parsed.apkUrl).pathname, { method: 'HEAD', signal })
        const bytes = Number(head.headers.get('content-length'))
        if (head.ok && bytes > 0 && !signal.aborted) setSizeBytes(bytes)
      } catch {
        // Offline, a host without the channel, or a navigation away: the
        // card keeps linking the install page, which is never wrong about
        // the current build.
      }
    }

    load()
    return () => controller.abort()
  }, [])

  return { manifest, sizeBytes }
}

/** Which install path this browser gets — decided once, from the store and the device. */
function useWebInstall() {
  const deferredEvent = useInstallStore((s) => s.deferredEvent)
  const installed = useInstallStore((s) => s.installed)
  // A lazy initialiser rather than an effect: both checks are safe without a
  // window (they answer false), so the first paint is already the right one.
  const [device] = useState(() => ({ standalone: isStandalone(), ios: isIos() }))
  return webInstallState({ ...device, installed, canPrompt: Boolean(deferredEvent) })
}

function DownloadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" />
      <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </svg>
  )
}

function AndroidCard({ manifest, sizeBytes }) {
  const { t } = useTranslation('download')
  const chips = manifest ? androidChips({ versionName: manifest.versionName, sizeBytes }) : []

  return (
    <section className={CARD} aria-labelledby="download-android">
      <h2 id="download-android" className="text-lg font-bold text-ink">
        {t('android.title')}
      </h2>
      <p className="mt-1 text-sm leading-7 text-ink-2">{t('android.body')}</p>

      {chips.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={t('android.chipsLabel')}>
          {chips.map((chip) => (
            <li key={chip} className={CHIP}>
              {chip}
            </li>
          ))}
        </ul>
      )}

      {manifest?.notes && (
        <p className="mt-3 text-sm leading-7 text-ink-2">
          <span className="font-medium text-ink">{t('android.whatsNew')}</span>
          {manifest.notes}
        </p>
      )}

      <div className="mt-auto pt-5">
        {/* Until latest.json has answered, the button goes to the install
            page — the same file, one tap further — instead of nowhere. */}
        <a href={manifest ? manifest.apkUrl : UPDATES_URL} download={manifest ? true : undefined} className={PRIMARY}>
          <DownloadIcon />
          {t('android.button')}
        </a>
        <BazaarButton className={`${SECONDARY} mt-3`} />
        <a href={UPDATES_URL} className="mt-3 block text-center text-sm text-brand-700 hover:underline">
          {t('android.guide')}
        </a>
      </div>
    </section>
  )
}

function WebCard({ state, onInstall }) {
  const { t } = useTranslation('download')
  return (
    <section className={CARD} aria-labelledby="download-web">
      <h2 id="download-web" className="text-lg font-bold text-ink">
        {t('web.title')}
      </h2>
      <p className="mt-1 text-sm leading-7 text-ink-2">{t('web.body')}</p>

      <ul className="mt-3 flex flex-wrap gap-2" aria-label={t('web.chipsLabel')}>
        <li className={CHIP}>{t('chips.version', { version: faVersion(APP_VERSION) })}</li>
        <li className={CHIP}>{t('web.devices')}</li>
      </ul>

      <div className="mt-auto pt-5">
        {state === 'standalone' && (
          <>
            <StatusBanner tone="good" icon="check">
              {t('web.standalone')}
            </StatusBanner>
            <Link to="/auth" className={`${PRIMARY} mt-3`}>
              {t('web.startPractice')}
            </Link>
          </>
        )}

        {state === 'installed' && (
          <>
            <StatusBanner tone="good" icon="check">
              {t('web.installed')}
            </StatusBanner>
            <Link to="/auth" className={`${SECONDARY} mt-3`}>
              {t('web.continueHere')}
            </Link>
          </>
        )}

        {state === 'prompt' && (
          <>
            <Button size="lg" className="w-full" onClick={onInstall}>
              {t('web.install')}
            </Button>
            <Link to="/auth" className="mt-3 block text-center text-sm text-brand-700 hover:underline">
              {t('web.openInBrowser')}
            </Link>
          </>
        )}

        {(state === 'ios' || state === 'manual') && (
          <>
            <Link to="/auth" className={PRIMARY}>
              {t('web.open')}
            </Link>
            <p className="mt-3 text-center text-xs leading-6 text-ink-2">{state === 'ios' ? t('web.hintIos') : t('web.hintManual')}</p>
          </>
        )}
      </div>
    </section>
  )
}

export default function Download() {
  const { t } = useTranslation('download')
  const { t: site } = useTranslation('site')
  const { manifest, sizeBytes } = useLatestBuild()
  const web = useWebInstall()
  const prompt = useInstallStore((s) => s.prompt)

  return (
    <SiteShell title={site('pageTitle.download')}>
      <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
        <nav aria-label={t('back')}>
          <Link to="/" className="text-sm text-ink-2 transition-colors hover:text-ink">
            {t('back')}
          </Link>
        </nav>

        <header className="mt-8 text-center">
          <ParrotMark size={72} className="mx-auto text-brand-600" />
          <h1 className="mt-4 text-3xl font-bold text-ink sm:text-4xl">{t('title')}</h1>
          <p className="mx-auto mt-3 max-w-xl leading-8 text-ink-2">{t('lead')}</p>
        </header>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <AndroidCard manifest={manifest} sizeBytes={sizeBytes} />
          <WebCard state={web} onInstall={prompt} />
        </div>

        <section className="mt-8 rounded-2xl border border-line bg-surface p-5" aria-labelledby="download-which">
          <h2 id="download-which" className="font-semibold text-ink">
            {t('which.title')}
          </h2>
          <dl className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-ink">{t('which.androidTitle')}</dt>
              <dd className="mt-1 leading-7 text-ink-2">{t('which.androidBody')}</dd>
            </div>
            <div>
              <dt className="font-medium text-ink">{t('which.webTitle')}</dt>
              <dd className="mt-1 leading-7 text-ink-2">{t('which.webBody')}</dd>
            </div>
          </dl>
        </section>
      </div>
    </SiteShell>
  )
}
